// Oliver Sweeney — Collection (Search & Discovery) page
// Depends on: os-global.js (for initComponents)
// Native Shopify filters/sort, updated via the section-rendering API so changing
// a filter or sort option doesn't trigger a full page reload.

class CollectionFilters {
  constructor(elem) {
    this._elem      = elem
    this._form      = elem.querySelector('[data-collection-filter-form]')
    this._sectionId = elem.dataset.sectionId
  }
  onInit() {
    if (!this._form) return
    this._form.addEventListener('submit', e => {
      e.preventDefault()
      this._update()
    })
    this._form.addEventListener('change', e => {
      if (e.target.matches('select, input[type="checkbox"], input[type="number"]')) this._update()
    })
  }
  _update() {
    const params = new URLSearchParams(new FormData(this._form))
    for (const [key, value] of Array.from(params.entries())) {
      if (value === '') params.delete(key)
    }
    const url = `${this._form.action}?${params.toString()}`
    fetch(`${url}&section_id=${this._sectionId}`)
      .then(res => res.text())
      .then(html => this._replace(html, url))
  }
  _replace(html, url) {
    const doc = new DOMParser().parseFromString(html, 'text/html')

    // Snapshot which filter-group accordions are open so we can restore them after replacement
    const openGroups = new Set()
    this._elem.querySelectorAll('.collection-sd__filter-group-toggle.is-open').forEach(btn => {
      const labelSpan = btn.querySelector('span')
      const label = labelSpan && labelSpan.textContent ? labelSpan.textContent.trim() : undefined
      if (label) openGroups.add(label)
    })

    const regions = [
      '[data-collection-toolbar]',
      '[data-collection-filters]',
      '[data-collection-grid]',
      '[data-collection-pagination]'
    ]
    regions.forEach(selector => {
      const next    = doc.querySelector(selector)
      const current = this._elem.querySelector(selector)
      if (next && current) {
        if (selector === '[data-collection-filters]' && current.classList.contains('is-open')) {
          next.classList.add('is-open')
        }
        current.replaceWith(next)
      }
    })

    // Restore open accordions without animation (the new body nodes start display:none)
    if (openGroups.size) {
      this._elem.querySelectorAll('.collection-sd__filter-group-toggle').forEach(btn => {
        const labelSpan = btn.querySelector('span')
      const label = labelSpan && labelSpan.textContent ? labelSpan.textContent.trim() : undefined
        if (label && openGroups.has(label)) {
          btn.classList.add('is-open')
          const body = btn.nextElementSibling
          if (body) body.style.display = 'block'
        }
      })
    }

    window.history.pushState({}, '', url)
    if (typeof window.initComponents === 'function') window.initComponents(this._elem)
    document.dispatchEvent(new CustomEvent('collectionUpdate'))
  }
}

// ─── LoadMore ────────────────────────────────────────────────────────────────
class LoadMore {
  constructor(sectionElem) {
    this._section   = sectionElem
    this._sectionId = sectionElem.dataset.sectionId
  }

  onInit() {
    // Take over scroll restoration so we can do it ourselves after pages re-append
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual'

    this._section.addEventListener('click', e => {
      const btn = e.target.closest('[data-loadmore-url]')
      if (!btn || btn.disabled) return
      btn.disabled    = true
      btn.textContent = 'Loading…'
      this._fetch(btn.dataset.loadmoreUrl).then(html => this._append(html, true))
    })

    // Save scroll position when the user navigates to a product page
    this._section.addEventListener('click', e => {
      if (e.target.closest('.collection-tile a')) {
        sessionStorage.setItem('os_scroll_pos', window.scrollY)
        sessionStorage.setItem('os_scroll_url', window.location.pathname)
      }
    })

    this._restore()
  }

  async _restore() {
    const loadedTo = parseInt(new URLSearchParams(window.location.search).get('loaded_to'), 10)
    if (!loadedTo || loadedTo < 2) return
    for (let page = 2; page <= loadedTo; page++) {
      const html = await this._fetch(this._pageUrl(page))
      this._append(html, false)
    }
    // Scroll back to where the user was when they left for the product page
    const savedPos = sessionStorage.getItem('os_scroll_pos')
    const savedUrl = sessionStorage.getItem('os_scroll_url')
    if (savedPos && savedUrl === window.location.pathname) {
      sessionStorage.removeItem('os_scroll_pos')
      sessionStorage.removeItem('os_scroll_url')
      window.scrollTo({ top: parseInt(savedPos, 10), behavior: 'instant' })
    }
  }

  _pageUrl(page) {
    const params = new URLSearchParams(window.location.search)
    params.delete('loaded_to')
    params.set('page', page)
    return `${window.location.pathname}?${params.toString()}`
  }

  _fetch(url) {
    return fetch(`${url}&section_id=${this._sectionId}`).then(r => r.text())
  }

  _append(html, trackUrl) {
    const doc     = new DOMParser().parseFromString(html, 'text/html')
    const newGrid = doc.querySelector('[data-collection-grid]')
    const grid    = this._section.querySelector('[data-collection-grid]')
    if (newGrid && grid) {
      newGrid.querySelectorAll('.collection-tile').forEach(tile => {
        grid.appendChild(tile)
      })
    }
    const newPagination = doc.querySelector('[data-collection-pagination]')
    const pagination    = this._section.querySelector('[data-collection-pagination]')
    if (newPagination && pagination) pagination.replaceWith(newPagination)

    if (trackUrl) {
      // Record the highest page loaded so back-navigation can restore it
      const prevPage = parseInt(new URLSearchParams(window.location.search).get('loaded_to'), 10) || 1
      const params = new URLSearchParams(window.location.search)
      params.set('loaded_to', prevPage + 1)
      history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
    }

    if (typeof window.initComponents === 'function') window.initComponents(grid)
    document.dispatchEvent(new CustomEvent('collectionUpdate'))
  }
}

// ─── ViewToggles ─────────────────────────────────────────────────────────────
class ViewToggles {
  constructor(elem) {
    this._toggles = elem
    this._layout  = document.querySelector('[data-collection-layout]')
  }
  onInit() {
    if (!this._layout) return

    const defaultDesktop = 4
    const defaultMobile  = 2
    const savedDesktop   = parseInt(localStorage.getItem('os_view_desktop'), 10) || defaultDesktop
    const savedMobile    = parseInt(localStorage.getItem('os_view_mobile'),  10) || defaultMobile

    this._apply(savedDesktop, savedMobile)

    this._toggles.addEventListener('click', e => {
      const btn = e.target.closest('[data-view-desktop],[data-view-mobile]')
      if (!btn) return
      const desktop = btn.dataset.viewDesktop ? parseInt(btn.dataset.viewDesktop, 10) : null
      const mobile  = btn.dataset.viewMobile  ? parseInt(btn.dataset.viewMobile,  10) : null
      const curDesktop = parseInt(this._layout.style.getPropertyValue('--per-row'), 10)
      const curMobile  = parseInt(this._layout.style.getPropertyValue('--per-row-mobile'), 10)
      this._apply(desktop || curDesktop, mobile || curMobile)
      if (desktop) localStorage.setItem('os_view_desktop', desktop)
      if (mobile)  localStorage.setItem('os_view_mobile',  mobile)
    })
  }
  _apply(desktop, mobile) {
    this._layout.style.setProperty('--per-row',        desktop)
    this._layout.style.setProperty('--per-row-open',   Math.max(1, desktop - 1))
    this._layout.style.setProperty('--per-row-mobile', mobile)
    this._layout.dataset.desktopView = desktop
    this._layout.dataset.mobileView = mobile

    this._toggles.querySelectorAll('[data-view-desktop]').forEach(btn => {
      btn.classList.toggle('is-active', parseInt(btn.dataset.viewDesktop, 10) === desktop)
    })
    this._toggles.querySelectorAll('[data-view-mobile]').forEach(btn => {
      btn.classList.toggle('is-active', parseInt(btn.dataset.viewMobile, 10) === mobile)
    })
  }
}

// ─── SearchRelated ──────────────────────────────────────────────────────────
// On the search results page, fetches matching collections and pages via the
// Predictive Search API and renders them above the product grid.
class SearchRelated {
  constructor(elem) {
    this._elem = elem
  }
  onInit() {
    const q = new URLSearchParams(window.location.search).get('q')
    if (!q) return
    fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=collection,page&resources[limit]=8`)
      .then(r => r.json())
      .then(data => {
        const results     = (data && data.resources && data.resources.results) || {}
        const collections = results.collections || []
        const pages       = results.pages || []
        if (!collections.length && !pages.length) return
        this._render(collections, pages)
      })
      .catch(() => {})
  }
  _render(collections, pages) {
    let html = ''
    if (collections.length) {
      html += `<div class="search-related__section">
        <p class="search-related__label">Collections</p>
        <div class="search-related__pages">`
      collections.forEach(c => {
        html += `<a href="${c.url}" class="search-related__page">${c.title}</a>`
      })
      html += `</div></div>`
    }
    if (pages.length) {
      html += `<div class="search-related__section">
        <p class="search-related__label">Pages</p>
        <div class="search-related__pages">`
      pages.forEach(pg => {
        html += `<a href="${pg.url}" class="search-related__page">${pg.title}</a>`
      })
      html += `</div></div>`
    }
    this._elem.className = 'container search-related'
    this._elem.removeAttribute('style')
    this._elem.innerHTML = html
  }
}

// ─── PriceSlider ─────────────────────────────────────────────────────────────
class PriceSlider {
  constructor(el) {
    this._boundMin   = parseFloat(el.dataset.priceBoundMin)
    this._boundMax   = parseFloat(el.dataset.priceBoundMax)
    this._displayMin = parseFloat(el.dataset.priceDisplayMin || el.dataset.priceBoundMin)
    this._inputMin   = el.querySelector('[data-price-input-min]')
    this._inputMax   = el.querySelector('[data-price-input-max]')
    this._thumbMin   = el.querySelector('[data-price-thumb-min]')
    this._thumbMax   = el.querySelector('[data-price-thumb-max]')
    this._fill       = el.querySelector('[data-price-fill]')
  }
  onInit() {
    if (!this._thumbMin || !this._thumbMax) return
    this._update()

    this._thumbMin.addEventListener('input', () => {
      // Snap back to cheapest available price if dragged below it
      let lo = parseFloat(this._thumbMin.value)
      if (lo < this._displayMin) lo = this._displayMin
      if (lo > parseFloat(this._thumbMax.value)) lo = parseFloat(this._thumbMax.value)
      this._thumbMin.value = lo
      this._inputMin.value = lo
      this._update()
    })
    this._thumbMax.addEventListener('input', () => {
      if (parseFloat(this._thumbMax.value) < parseFloat(this._thumbMin.value))
        this._thumbMax.value = this._thumbMin.value
      this._inputMax.value = this._thumbMax.value
      this._update()
    })
    this._thumbMin.addEventListener('change', () => {
      if (parseFloat(this._thumbMin.value) <= this._displayMin) this._inputMin.value = ''
      this._inputMin.dispatchEvent(new Event('change', { bubbles: true }))
    })
    this._thumbMax.addEventListener('change', () => {
      if (parseFloat(this._thumbMax.value) >= this._boundMax) this._inputMax.value = ''
      this._inputMax.dispatchEvent(new Event('change', { bubbles: true }))
    })

    this._inputMin.addEventListener('input', () => {
      this._thumbMin.value = this._inputMin.value !== '' ? this._inputMin.value : this._displayMin
      this._update()
    })
    this._inputMax.addEventListener('input', () => {
      this._thumbMax.value = this._inputMax.value !== '' ? this._inputMax.value : this._boundMax
      this._update()
    })
  }
  _update() {
    const lo    = parseFloat(this._thumbMin.value)
    const hi    = parseFloat(this._thumbMax.value)
    const range = this._boundMax - this._boundMin
    if (range > 0) {
      const leftPct  = (lo - this._boundMin) / range * 100
      const widthPct = Math.max(0, hi - lo) / range * 100
      this._fill.style.left  = leftPct.toFixed(1) + '%'
      this._fill.style.width = widthPct.toFixed(1) + '%'
    }
    // When min thumb reaches max position, elevate it so it can be dragged back left
    this._thumbMin.style.zIndex = lo >= hi ? 3 : 1
    this._thumbMax.style.zIndex = 2
  }
}

// ─── BackToTop ────────────────────────────────────────────────────────────────
class BackToTop {
  constructor(elem) {
    this._elem = elem
  }
  onInit() {
    const toggle = () => this._elem.classList.toggle('is-visible', window.scrollY > 800)
    window.addEventListener('scroll', toggle, { passive: true })
    toggle()
    this._elem.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }))
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Keep --sd-header-h in sync with the actual header height so the sticky
  // filter panel stops flush with the bottom of the header on scroll.
  const toolbar = document.querySelector('[data-collection-toolbar]')
  function updateToolbarH() {
    document.documentElement.style.setProperty(
      '--sd-toolbar-h', (toolbar ? toolbar.offsetHeight : 0) + 'px'
    )
  }
  updateToolbarH()
  window.addEventListener('resize', updateToolbarH)

  document.querySelectorAll('[data-collection-search-discovery]').forEach(el => {
    new CollectionFilters(el).onInit()
    new LoadMore(el).onInit()
  })
  document.querySelectorAll('[data-search-related]').forEach(el =>
    new SearchRelated(el).onInit())
  document.querySelectorAll('[data-back-to-top]').forEach(el =>
    new BackToTop(el).onInit())
  function initViewToggles() {
    document.querySelectorAll('[data-view-toggles]').forEach(el => {
      if (el._viewTogglesInit) return
      el._viewTogglesInit = true
      new ViewToggles(el).onInit()
    })
  }
  initViewToggles()
  document.addEventListener('collectionUpdate', initViewToggles)

  function updateAvailabilityTotal() {
    const form  = document.querySelector('[data-collection-filter-form]')
    const total = form && form.dataset.availabilityTotal
    if (!total) return
    document.querySelectorAll('[data-availability-total-display]').forEach(el => {
      el.textContent = total
    })
  }
  updateAvailabilityTotal()
  document.addEventListener('collectionUpdate', updateAvailabilityTotal)

  function initPriceSliders() {
    document.querySelectorAll('[data-price-slider]').forEach(el => {
      if (el._priceSliderInit) return
      el._priceSliderInit = true
      new PriceSlider(el).onInit()
    })
  }
  initPriceSliders()
  document.addEventListener('collectionUpdate', initPriceSliders)

  function updateSearchFilterTitle() {
    const layout = document.querySelector('[data-collection-layout][data-search-total]')
    if (!layout) return
    const titleEl = layout.querySelector('[data-filtered-count]')
    if (!titleEl) return
    titleEl.textContent = `Filtered to ${titleEl.dataset.filteredCount} of ${layout.dataset.searchTotal}`
  }
  document.addEventListener('collectionUpdate', updateSearchFilterTitle)

  // Mobile: swipe left/right slides to the other tile image, circular.
  // Transform-based so the animation always moves in the swipe direction.
  function tileSwipe(container, direction) {
    const primary   = container.querySelector('.collection-tile__img:not(.collection-tile__img--secondary)')
    const secondary = container.querySelector('.collection-tile__img--secondary')
    if (!primary || !secondary) return
    const showingSecondary = container.dataset.tileSecondary === '1'
    const outgoing = showingSecondary ? secondary : primary
    const incoming = showingSecondary ? primary   : secondary
    const startX = direction === 'left' ? '100%' : '-100%'
    const exitX  = direction === 'left' ? '-100%' : '100%'
    incoming.style.transition = 'none'
    incoming.style.transform  = `translateX(${startX})`
    incoming.offsetHeight // force reflow
    incoming.style.transition = ''
    incoming.style.transform  = 'translateX(0)'
    outgoing.style.transform  = `translateX(${exitX})`
    container.dataset.tileSecondary = showingSecondary ? '0' : '1'
  }

  let swipeTouchX = null
  let swipeTarget = null
  document.addEventListener('touchstart', e => {
    const wrap = e.target.closest('.collection-tile__images')
    if (!wrap || !wrap.querySelector('.collection-tile__img--secondary')) return
    swipeTarget = wrap
    swipeTouchX = e.touches[0].clientX
  }, { passive: true })
  document.addEventListener('touchend', e => {
    if (!swipeTarget || swipeTouchX === null) return
    const delta = e.changedTouches[0].clientX - swipeTouchX
    swipeTouchX = null
    const wrap = swipeTarget
    swipeTarget = null
    if (window.innerWidth >= 769 || Math.abs(delta) < 30) return
    tileSwipe(wrap, delta < 0 ? 'left' : 'right')
  }, { passive: true })

  // 3-wide mobile: tapping the product image triggers quick add instead of navigation
  document.addEventListener('click', e => {
    const layout = document.querySelector('[data-collection-layout]')
    if (!layout || layout.dataset.mobileView !== '3') return
    if (window.innerWidth >= 769) return
    const imageLink = e.target.closest('.collection-tile__images')
    if (!imageLink) return
    e.preventDefault()
    const tile = imageLink.closest('.collection-tile')
    const quickAdd = tile && tile.querySelector('[data-quick-add]')
    if (quickAdd) quickAdd.click()
  })
})
